import { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  BarChart, Bar, AreaChart, Area, XAxis, YAxis, CartesianGrid
} from 'recharts';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '../ui/chart';
import { AXIS } from './chartTheme';
import { tipFormatter, AreaGradient } from './ChartKit';

/**
 * Seccion compartida para Servicios (tipo='servicios') y Productos (tipo='productos'):
 * KPIs con delta, curva mensual unidades/$ (activables), top del periodo con margen,
 * y desglose por categoria (solo productos).
 */

const money = (n: number) => {
  if (n == null) return '—';
  if (Math.abs(n) >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
  if (Math.abs(n) >= 1e3) return `$${Math.round(n / 1e3)}k`;
  return `$${Math.round(n).toLocaleString('es-AR')}`;
};

function DeltaChip({ value }: { value: number | null }) {
  if (value == null) return <span className="text-xs text-muted-foreground">sin ref.</span>;
  const color = value > 0 ? 'text-green-600 bg-green-500/10' : value < 0 ? 'text-red-600 bg-red-500/10' : 'text-muted-foreground bg-muted';
  return <span className={`text-xs px-1.5 py-0.5 rounded ${color}`}>{value > 0 ? '▲ +' : value < 0 ? '▼ ' : ''}{value}% vs per. ant.</span>;
}

export default function ItemsSection({ tipo, startDate, endDate }: { tipo: 'servicios' | 'productos'; startDate: string; endDate: string }) {
  const [d, setD] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [metric, setMetric] = useState<'revenue' | 'unidades'>('revenue');
  const [catMetric, setCatMetric] = useState<'revenue' | 'unidades'>('revenue');
  // Filtro de categoria para la curva mensual (solo productos)
  const [serieCat, setSerieCat] = useState('todas');
  const [serieFiltrada, setSerieFiltrada] = useState<any[] | null>(null);
  const [serieLoading, setSerieLoading] = useState(false);

  const esServicio = tipo === 'servicios';
  const unidadLabel = esServicio ? 'trabajos' : 'unidades';

  useEffect(() => {
    if (!startDate || !endDate || startDate > endDate) return;
    setError('');
    setLoading(true);
    axios.get(`/api/reports/tablero/${tipo}`, { params: { startDate, endDate } })
      .then((r) => setD(r.data))
      .catch((e) => setError(e.response?.data?.error || 'Error cargando datos'))
      .finally(() => setLoading(false));
  }, [tipo, startDate, endDate]);

  // Serie mensual filtrada por categoria: pedido aparte para no tocar KPIs/top
  useEffect(() => {
    if (serieCat === 'todas') { setSerieFiltrada(null); return; }
    setSerieLoading(true);
    axios.get(`/api/reports/tablero/${tipo}`, { params: { startDate, endDate, category: serieCat } })
      .then((r) => setSerieFiltrada(r.data.serie))
      .catch(() => setSerieFiltrada(null))
      .finally(() => setSerieLoading(false));
  }, [serieCat, tipo, startDate, endDate]);

  if (loading && !d) return <div className="py-10 text-center text-muted-foreground">Cargando {tipo}...</div>;
  if (error) return <div className="py-10 text-center text-red-600">{error}</div>;
  if (!d) return null;

  return (
    <div className={`space-y-4 transition-opacity duration-200 ${loading ? 'opacity-60 pointer-events-none' : ''}`}>
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">
        <Card><CardContent className="pt-4 pb-3">
          <p className="text-xs text-muted-foreground mb-1">{esServicio ? 'Trabajos vendidos' : 'Unidades vendidas'}</p>
          <p className="text-2xl font-semibold leading-none mb-1.5">{d.kpis.unidades.value.toLocaleString('es-AR')}</p>
          <DeltaChip value={d.kpis.unidades.deltaPct} />
        </CardContent></Card>
        <Card><CardContent className="pt-4 pb-3">
          <p className="text-xs text-muted-foreground mb-1">Facturación</p>
          <p className="text-2xl font-semibold leading-none mb-1.5">{money(d.kpis.revenue.value)}</p>
          <DeltaChip value={d.kpis.revenue.deltaPct} />
        </CardContent></Card>
        <Card><CardContent className="pt-4 pb-3">
          <p className="text-xs text-muted-foreground mb-1">{esServicio ? 'Precio promedio por trabajo' : 'Precio promedio por unidad'}</p>
          <p className="text-2xl font-semibold leading-none mb-1.5">{money(d.kpis.ticket.value)}</p>
          <span className="text-xs text-muted-foreground">en el período</span>
        </CardContent></Card>
        <Card><CardContent className="pt-4 pb-3">
          <p className="text-xs text-muted-foreground mb-1">{esServicio ? 'Servicios distintos' : 'Margen promedio'}</p>
          <p className="text-2xl font-semibold leading-none mb-1.5">
            {esServicio ? d.kpis.distintos.value : (d.kpis.margen.value != null ? `${d.kpis.margen.value}%` : '—')}
          </p>
          <span className="text-xs text-muted-foreground">{esServicio ? 'vendidos en el período' : 'sobre items con costo cargado'}</span>
        </CardContent></Card>
      </div>

      <Card>
        <CardContent className="pt-5">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
            <h3 className="font-semibold text-sm">Evolución mensual</h3>
            <div className="flex items-center gap-2 text-xs">
              {d.porCategoria.length > 0 && (
                <Select value={serieCat} onValueChange={setSerieCat}>
                  <SelectTrigger className="h-6 w-[150px] text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todas">{esServicio ? 'Todas las áreas' : 'Todas las categorías'}</SelectItem>
                    {d.porCategoria.map((c: any) => (
                      <SelectItem key={c.categoria} value={c.categoria}>{c.categoria}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              <button onClick={() => setMetric('revenue')} className={`px-2 py-0.5 rounded border ${metric === 'revenue' ? 'bg-primary text-primary-foreground border-primary' : 'border-border text-muted-foreground'}`}>$</button>
              <button onClick={() => setMetric('unidades')} className={`px-2 py-0.5 rounded border ${metric === 'unidades' ? 'bg-primary text-primary-foreground border-primary' : 'border-border text-muted-foreground'}`}>{unidadLabel}</button>
            </div>
          </div>
          <ChartContainer
            config={{
              revenue: { label: 'Facturación', color: 'hsl(var(--chart-2))' },
              unidades: { label: 'Cantidad', color: 'hsl(var(--chart-2))' }
            }}
            className={`h-[230px] w-full transition-opacity duration-200 ${serieLoading ? 'opacity-60' : ''}`}
          >
            <AreaChart data={serieCat === 'todas' ? d.serie : (serieFiltrada || [])} margin={{ top: 4, right: 8, bottom: 0, left: 8 }}>
              <defs>
                <AreaGradient id="fillItems" color={`var(--color-${metric})`} />
              </defs>
              <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.25} vertical={false} />
              <XAxis dataKey="label" {...AXIS} />
              <YAxis {...AXIS} tickFormatter={(v: number) => metric === 'revenue' ? money(v) : String(v)} width={56} allowDecimals={false} />
              <ChartTooltip content={<ChartTooltipContent formatter={tipFormatter(
                { revenue: 'Facturación', unidades: 'Cantidad' },
                (v) => metric === 'revenue' ? money(v) : `${v} ${unidadLabel}`
              )} />} />
              <Area type="monotone" dataKey={metric} stroke={`var(--color-${metric})`} strokeWidth={2.4} fill="url(#fillItems)" dot={{ r: 3 }} />
            </AreaChart>
          </ChartContainer>
        </CardContent>
      </Card>

      <div className={`grid grid-cols-1 ${esServicio ? '' : 'lg:grid-cols-2'} gap-4`}>
        <Card>
          <CardContent className="pt-5">
            <h3 className="font-semibold text-sm mb-3">Top del período (por facturación)</h3>
            {d.top.length === 0 && <p className="text-sm text-muted-foreground">Sin ventas en el período</p>}
            {d.top.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-xs text-muted-foreground text-left">
                      <th className="pb-1.5 font-normal">{esServicio ? 'Servicio' : 'Producto'}</th>
                      <th className="pb-1.5 font-normal text-right">Cant.</th>
                      <th className="pb-1.5 font-normal text-right">Facturado</th>
                      <th className="pb-1.5 font-normal text-right">Margen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.top.map((t: any, i: number) => (
                      <tr key={i} className="border-t">
                        <td className="py-1.5 pr-2 truncate max-w-[260px]">{t.name}</td>
                        <td className="py-1.5 pr-2 text-right text-muted-foreground">{t.unidades}</td>
                        <td className="py-1.5 pr-2 text-right font-medium">{money(t.revenue)}</td>
                        <td className={`py-1.5 text-right ${t.margenPct == null ? 'text-muted-foreground' : t.margenPct < 10 ? 'text-red-600' : t.margenPct < 20 ? 'text-amber-600' : 'text-green-600'}`}>
                          {t.margenPct != null ? `${t.margenPct}%` : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {!esServicio && (
          <Card>
            <CardContent className="pt-5">
              <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
                <h3 className="font-semibold text-sm">Por categoría</h3>
                <div className="flex gap-2 text-xs">
                  <button onClick={() => setCatMetric('revenue')} className={`px-2 py-0.5 rounded border ${catMetric === 'revenue' ? 'bg-primary text-primary-foreground border-primary' : 'border-border text-muted-foreground'}`}>$</button>
                  <button onClick={() => setCatMetric('unidades')} className={`px-2 py-0.5 rounded border ${catMetric === 'unidades' ? 'bg-primary text-primary-foreground border-primary' : 'border-border text-muted-foreground'}`}>{unidadLabel}</button>
                </div>
              </div>
              {d.porCategoria.length === 0 && <p className="text-sm text-muted-foreground">Sin ventas en el período</p>}
              {d.porCategoria.length > 0 && (
                <ChartContainer
                  config={{
                    revenue: { label: 'Facturado', color: 'hsl(var(--chart-1))' },
                    unidades: { label: 'Cantidad', color: 'hsl(var(--chart-1))' }
                  }}
                  className="w-full"
                  style={{ height: Math.max(160, d.porCategoria.length * 34) }}
                >
                  <BarChart
                    data={[...d.porCategoria].sort((a: any, b: any) => Number(b[catMetric]) - Number(a[catMetric]))}
                    layout="vertical" margin={{ top: 0, right: 40, bottom: 0, left: 8 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.25} horizontal={false} />
                    <XAxis type="number" {...AXIS} tickFormatter={(v: number) => catMetric === 'revenue' ? money(v) : String(v)} allowDecimals={false} />
                    <YAxis type="category" dataKey="categoria" {...AXIS} width={100} />
                    <ChartTooltip content={<ChartTooltipContent formatter={(v: any, _n: any, item: any) => (
                      <>
                        <span className="h-2.5 w-2.5 shrink-0 rounded-[2px]" style={{ background: item?.color || item?.payload?.fill }} />
                        <div className="flex flex-1 items-center justify-between gap-3 leading-none">
                          <span className="text-muted-foreground">{catMetric === 'revenue' ? 'Facturado' : 'Cantidad'}</span>
                          <span className="font-mono font-medium tabular-nums text-foreground">
                            {catMetric === 'revenue'
                              ? <>{money(Number(v))} · {item?.payload?.unidades} u</>
                              : <>{item?.payload?.unidades} u · {money(Number(item?.payload?.revenue))}</>}
                          </span>
                        </div>
                      </>
                    )} />} />
                    <Bar dataKey={catMetric} fill={`var(--color-${catMetric})`} radius={[0, 3, 3, 0]} maxBarSize={20} />
                  </BarChart>
                </ChartContainer>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
